import { DragAndDropService, GridApi } from '@ag-grid-community/core'

/** The grid's bean container. Autowired onto the api object, but not part of its public type. */
interface GridBeanContext {
  getBean(name: string): unknown
}

/**
 * ag-Grid's `DragAndDropService` — the bean its own `agColumnsToolPanel` uses to make panel rows
 * draggable onto the grid.
 *
 * A drop only lands if the dragged item comes from that service with `DragSourceType.ToolPanel`:
 * the grid's header/body drop target ignores everything else, which is why hand-rolled drag (CDK,
 * HTML5 DnD) silently does nothing. The service is not on the public `GridApi` surface, but the
 * grid autowires its bean context onto the api object, so a custom tool panel can reach it.
 *
 * Returns undefined instead of throwing — without it the panel loses drag but still works, which
 * is a better failure than a Column Manager that will not open.
 */
export function gridDragAndDropService(
  api: GridApi,
): DragAndDropService | undefined {
  const context = (api as unknown as { context?: GridBeanContext }).context
  return (
    (context?.getBean('dragAndDropService') as DragAndDropService) ?? undefined
  )
}

/**
 * The index to hand `columnApi.moveColumn` to land `colId` beside `targetColId`.
 *
 * `moveColumn` removes the column before re-inserting it, so a column travelling forwards lands one
 * slot early unless the target index is pulled back. Returns null when the target is unknown or the
 * move would be a no-op.
 */
export function resolveMoveIndex(
  colIds: string[],
  colId: string,
  targetColId: string,
  after: boolean,
): number | null {
  const targetIndex = colIds.indexOf(targetColId)
  if (targetIndex < 0) return null

  let toIndex = after ? targetIndex + 1 : targetIndex
  const fromIndex = colIds.indexOf(colId)
  if (fromIndex >= 0 && fromIndex < toIndex) toIndex -= 1

  return fromIndex === toIndex ? null : toIndex
}
